import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { fecha } from "../lib/formato";
import { useIdioma } from "../lib/idioma";

/**
 * Lo que el socio opina de lo que se ha llevado.
 *
 * Solo aparece lo que ha retirado de verdad. Una nota de quien no ha probado el
 * producto no dice nada, y ensuciaría la media que el club usa para decidir qué
 * vuelve a pedir.
 *
 * Se puntúa tocando una estrella, sin botón de guardar: en un móvil, cada paso
 * de más es gente que lo deja a medias. El comentario sí se guarda aparte,
 * porque escribir lleva tiempo y no conviene enviar a mitad de frase.
 */

interface Valorable {
  productoId: string;
  nombre: string;
  ultima: string;
  mia: { puntuacion: number; comentario: string | null } | null;
}

export default function PortalValoraciones() {
  const clienteConsultas = useQueryClient();
  const { t } = useIdioma();
  const [comentarios, setComentarios] = useState<Record<string, string>>({});
  const [guardado, setGuardado] = useState<string | null>(null);

  const lista = useQuery({
    queryKey: ["portal-valoraciones"],
    queryFn: () => api<Valorable[]>("/portal/valoraciones"),
  });

  async function valorar(
    v: Valorable,
    puntuacion: number,
    comentario?: string,
  ) {
    await api("/portal/valoraciones", {
      metodo: "POST",
      cuerpo: {
        productoId: v.productoId,
        puntuacion,
        comentario:
          comentario ??
          comentarios[v.productoId] ??
          v.mia?.comentario ??
          undefined,
      },
    });
    setGuardado(v.productoId);
    setTimeout(() => setGuardado(null), 2000);
    void clienteConsultas.invalidateQueries({
      queryKey: ["portal-valoraciones"],
    });
  }

  if (lista.isLoading)
    return <div className="cargando">{t("general.cargando")}</div>;
  const valorables = lista.data ?? [];

  if (valorables.length === 0) {
    return (
      <section className="portal-tarjeta">
        <p className="suave" style={{ margin: 0 }}>
          {t("valoraciones.vacio")}
        </p>
      </section>
    );
  }

  return (
    <>
      <p className="suave pequeno">{t("valoraciones.intro")}</p>

      {valorables.map((v) => (
        <section key={v.productoId} className="portal-tarjeta">
          <div className="fila separada">
            <strong>{v.nombre}</strong>
            <span className="suave pequeno">{fecha(v.ultima)}</span>
          </div>

          <div className="estrellas">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                className={`estrella${(v.mia?.puntuacion ?? 0) >= n ? " puesta" : ""}`}
                aria-label={t("valoraciones.nDe5", { n })}
                onClick={() => void valorar(v, n)}
              >
                ★
              </button>
            ))}
            {guardado === v.productoId && (
              <span className="suave pequeno">{t("general.guardado")}</span>
            )}
          </div>

          {v.mia && (
            <div className="fila" style={{ marginTop: 8 }}>
              <input
                value={comentarios[v.productoId] ?? v.mia.comentario ?? ""}
                onChange={(e) =>
                  setComentarios((c) => ({
                    ...c,
                    [v.productoId]: e.target.value,
                  }))
                }
                placeholder={t("valoraciones.comentario")}
                maxLength={500}
              />
              <button
                className="boton secundario"
                onClick={() =>
                  void valorar(
                    v,
                    v.mia!.puntuacion,
                    comentarios[v.productoId] ?? "",
                  )
                }
              >
                {t("general.guardar")}
              </button>
            </div>
          )}
        </section>
      ))}
    </>
  );
}
